import { currentAccessToken } from "~/utils/keycloak";

import type { Route } from "~/routes/core/routes";

import type { CustomHeaders } from "~/types/fetch";

import { TEN_THOUSAND } from "~/composables/config";

type MethodType = "GET" | "POST" | "PUT" | "DELETE";

const ENABLE_LOCAL_CACHE = true;

const pendingPromises: Record<string, Promise<any>> = {};

/**
 * Normalizes a URL by removing double slashes (except after protocol)
 * @param url The URL to normalize
 * @returns The normalized URL
 */
function normalizeUrl (url: string): string {
	// Preserve protocol double slashes (http:// or https://)
	const protocolPattern = /^(https?:\/\/)/;
	const hasProtocol = protocolPattern.test(url);

	if (hasProtocol) {
		// Split URL into protocol and the rest
		const [ protocol, rest ] = url.split("://");
		// Replace multiple slashes with a single slash in the rest of the URL
		const normalizedRest = rest.replace(/\/+/g, "/");
		return `${protocol}://${normalizedRest}`;
	} else {
		// If no protocol, just replace multiple slashes with a single slash
		return url.replace(/\/+/g, "/");
	}
}

/**
 * Executes a custom fetch request with options for caching, headers, and request method customization.
 *
 * @param {string} requestUrl The URL to be requested. It will be normalized to remove double slashes.
 * @param {MethodType} [requestMethod="GET"] The HTTP method to use for the request. Defaults to "GET".
 * @param {CustomHeaders} [requestHeaders={}] Additional headers to include in the request.
 * @param {object | null} [requestBody=null] The body to include in the request, applicable for non-GET requests.
 * @param {boolean} [noBearer=false] Indicates whether to exclude the Bearer token Authorization header. Defaults to false.
 * @param {boolean} [withCache=true] Enables or disables caching of the request in local storage. Defaults to true.
 * @return {Promise} A promise that resolves with the response data of type T or null if an error occurs.
 */
export async function useCustomFetch<T> (
	requestUrl: string,
	requestMethod: MethodType = "GET",
	requestHeaders: CustomHeaders = {},
	requestBody: object | null = null,
	noBearer: boolean = false,
	withCache: boolean = true
): Promise<T> {
	try {
		// Normalize URL to remove double slashes
		requestUrl = normalizeUrl(requestUrl);
		const cacheKey = JSON.stringify({
			requestUrl,
			requestMethod,
			requestHeaders,
			requestBody
		});
		if (ENABLE_LOCAL_CACHE && withCache) {
			const cachedData = localStorage.getItem(cacheKey);
			if (cachedData) {
				const { data, timestamp } = JSON.parse(cachedData);
				const config = useRuntimeConfig().public;
				const isExpired
                    = Date.now() - timestamp
                    > parseInt(`${config.LOCAL_CACHE_DURATION ?? TEN_THOUSAND * 2}`);
				if (!isExpired) {
					return data;
				}
				localStorage.removeItem(cacheKey);
			}

			if (pendingPromises[cacheKey] !== undefined) {
				return await pendingPromises[cacheKey];
			}
		}

		const options: {
            method: MethodType;
            headers: CustomHeaders;
            body?: string;
        } = {
        	method: requestMethod,
        	headers: {
        		...requestHeaders
        	}
        };

		// @ts-ignore
		if (!noBearer && !options?.headers?.Authorization) {
			options.headers = {
				...options.headers,
				Authorization: `Bearer ${currentAccessToken.value}`
			};
		}

		if (requestMethod !== "GET" && requestBody !== null && options.headers) {
			options.body = JSON.stringify(requestBody);
			options.headers = {
				...options.headers,
				"Content-Type": "application/json"
			};
		}

		const promise = $fetch<T>(requestUrl, {
			...options
		}).then(data => {
			const result = data as T;
			if (ENABLE_LOCAL_CACHE && withCache) {
				localStorage.setItem(
					cacheKey,
					JSON.stringify({ data: result, timestamp: Date.now() })
				);
				delete pendingPromises[cacheKey];
			}
			return result;
		});

		pendingPromises[cacheKey] = promise;
		return await promise;
	} catch (e) {
		console.error(e);
		return null as unknown as T;
	}
}

type RequestObject = Record<string, any>;

function replaceUrlParams (url: string, requestBody: RequestObject): string {
	const regex = /\[(\w+)]/g;
	return url.replace(regex, (_, paramName: string) => {
		if (requestBody[paramName] !== undefined) {
			return requestBody[paramName];
		} else {
			throw new Error(`Missing value for URL parameter: ${paramName}`);
		}
	});
}

export async function useFetchRoute<T> (
	route: Route,
	urlParams: object | null = null,
	body: object | null = null,
	headers: CustomHeaders = {}
): Promise<T> {
	try {
		let url = replaceUrlParams(route.path, urlParams ?? {});
		// Normalize URL to remove double slashes
		url = normalizeUrl(url);
		const response = await useCustomFetch(
			url,
			route.method,
			{
				...headers,
				"Leviia-Route": route.path
			},
			body,
			false,
			route.withCache
		);

		return response as T;
	} catch (e) {
		console.error(e);
		return null as unknown as T;
	}
}

const PROTOCOL_HTTP = "http://";
const PROTOCOL_HTTPS = "https://";
const ROUTE_PARAM_REGEX = /\[(\w+)]/g;

export function normalizePath (path: string): string {
	path = path
		.replaceAll(ROUTE_PARAM_REGEX, "")
		.replaceAll(PROTOCOL_HTTP, "")
		.replaceAll(PROTOCOL_HTTPS, "");
	while (path.startsWith("/")) {
		path = path.slice(1);
	}
	while (path.endsWith("/")) {
		path = path.slice(0, -1);
	}
	return path;
}

export function clearCacheForRoute (route: Route): void {
	const normalizedPath = normalizePath(route.path);
	const localStorageKeys = Object.keys(localStorage);
	const cacheKeys = localStorageKeys.filter(key => key.includes(normalizedPath));

	cacheKeys.forEach(key => {
		localStorage.removeItem(key);
	});

	log(
		"Vider le cache pour le chemin:",
		normalizedPath,
		`${cacheKeys.length} éléments supprimés`
	);
}

export async function fetchWithCache (url: string, options: RequestInit): Promise<Response> {
	// Normalize URL to remove double slashes
	url = normalizeUrl(url);
	const cachedData = localStorage.getItem(url);
	const now = Date.now();

	if (cachedData) {
		const { response, timestamp } = JSON.parse(cachedData);
		// eslint-disable-next-line no-magic-numbers
		if (now - timestamp < 20000) {
			// Convertir la réponse en objet Response avant de la renvoyer
			return new Response(response);
		}
	}

	const response = await fetch(url, options);
	if (response.ok) {
		const responseData = await response.clone().text();
		localStorage.setItem(url, JSON.stringify({ response: responseData, timestamp: now }));
	}

	return response;
}
